import { PromotionHotelRow, PromotionMove, PromotionMoveNight, PromotionNight } from '@app/model/promotion/PromotionOverview';

const LABELS: Record<string, string> = {
	GETAWAY_DEAL: 'Getaway Deal',
	BONUS_SAVINGS: 'Bonus savings',
	LAST_MINUTE_DEAL: 'Last-minute Deal',
	EARLY_BOOKER_DEAL: 'Early Booker Deal',
	LIMITED_TIME_DEAL: 'Limited-time Deal',
	WEEKLY_RATE: 'Weekly rate',
	BOOKING_FUNDED: 'Booking.com-funded discount',
	UNKNOWN_PROMOTION: 'Other promotion',
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const promotionLabel = (code: string): string =>
	LABELS[code] ?? code.toLowerCase().split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

export const promotionPillLabel = (code: string): string =>
	code === 'BOOKING_FUNDED' ? 'Booking.com-funded' : promotionLabel(code);

export const formatDay = (iso: string): string => {
	const [, month, day] = iso.split('-');
	return `${Number(day)} ${MONTH_SHORT[Number(month) - 1]}`;
};

export const formatDayYear = (iso: string): string => {
	const [year, month, day] = iso.split('-');
	return `${Number(day)} ${MONTH_SHORT[Number(month) - 1]} ${year.slice(2)}`;
};

export const MOVE_VERB: Record<PromotionMove['action'], string> = {
	STARTED: 'Started',
	REMOVED: 'Removed',
	DEEPENED: 'Deepened',
	REDUCED: 'Reduced',
	INTERMITTENT: 'On and off',
	WINDOW_OPENED: 'Window opened',
	WINDOW_CLOSED: 'Window closed',
};

export const MOVE_MEANING: Record<PromotionMove['action'], string> = {
	STARTED: 'guests see the lower price there',
	REMOVED: 'that discount is gone there',
	DEEPENED: 'a bigger discount on the same rate',
	REDUCED: 'the discount got smaller; their shown price moved up',
	INTERMITTENT: 'the hotel switches this discount on and off',
	WINDOW_OPENED: 'likely entered its booking window close to arrival',
	WINDOW_CLOSED: 'likely left its booking window as arrival approaches',
};

export const isGain = (action: PromotionMove['action']): boolean =>
	action === 'STARTED' || action === 'DEEPENED';

export const isIntermittent = (move: PromotionMove): boolean => move.action === 'INTERMITTENT';

const intermittentState = (move: PromotionMove): string => {
	const onNights = move.nights.filter(night => night.percentageAfter != null).length;
	if (move.nights.length === 1) return onNights > 0 ? 'on now' : 'off now';
	return `on now for ${onNights} of ${move.nights.length} nights`;
};

export const isBookingWindow = (move: PromotionMove): boolean =>
	move.action === 'WINDOW_OPENED' || move.action === 'WINDOW_CLOSED';

export const moveTagState = (move: PromotionMove): 'success' | 'warning' | 'critical' | 'info' => {
	if (isIntermittent(move)) return 'warning';
	if (isBookingWindow(move)) return 'info';
	return isGain(move.action) ? 'success' : 'critical';
};

export const moveMeaning = (move: PromotionMove): string =>
	isIntermittent(move)
		? `${MOVE_MEANING.INTERMITTENT}; ${intermittentState(move)}`
		: MOVE_MEANING[move.action];

export const intermittentDepth = (move: PromotionMove): string => {
	const depth = move.medianPercentage == null ? '—' : `${Math.round(move.medianPercentage)}%`;
	return move.nights.length === 1
		? `${depth} · on ${move.seenInWindow} of ${move.windowScans} days`
		: `${depth} median`;
};

export const intermittentNightDepth = (night: PromotionMoveNight): string => {
	const percentage = night.percentageAfter ?? night.percentageBefore;
	const depth = percentage == null ? '—' : `${Math.round(percentage)}%`;
	return night.percentageAfter != null ? `${depth} · on now` : `${depth} last seen · off now`;
};

export const isHotelPaid = (row: PromotionHotelRow): boolean =>
	row.main || row.types.length === 0 || row.types.some(type => !type.bookingFunded);

export const TOP_MOVE_COUNT = 3;

export const depthChange = (before?: number | null, after?: number | null): string =>
	`${before == null ? 0 : Math.round(before)}% → ${after == null ? 0 : Math.round(after)}%`;

export const firstNightLead = (fromDate: string, today: Date = new Date()): string => {
	const [year, month, day] = fromDate.split('-').map(Number);
	const first = Date.UTC(year, month - 1, day);
	const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
	const days = Math.round((first - now) / 86400000);
	if (days < 0) return 'started already';
	if (days === 0) return 'tonight';
	if (days === 1) return 'tomorrow';
	return `in ${days} days`;
};

export const nightTintClass = (night: PromotionNight): string => {
	if (!night.tracked) return 'bg-gray-50';
	if (!night.available) return 'bg-gray-300';
	if (night.percentage == null || night.percentage === 0) return 'bg-white';
	if (night.percentage < 15) return 'bg-emerald-50';
	if (night.percentage < 35) return 'bg-emerald-100';
	if (night.percentage < 55) return 'bg-emerald-200';
	if (night.percentage < 75) return 'bg-emerald-300';
	return 'bg-emerald-400';
};

export const nightTooltip = (hotelName: string, night: PromotionNight): string => {
	if (!night.tracked) return `${hotelName} — ${formatDayYear(night.statDate)}: not tracked yet`;
	if (!night.available) return `${hotelName} — ${formatDayYear(night.statDate)}: sold out, so we cannot see a deal`;
	const minimumStay = night.minimumStay == null ? '' : ` · minimum stay ${night.minimumStay} nights`;
	if (night.percentage == null || night.percentage === 0)
		return `${hotelName} — ${formatDayYear(night.statDate)}: no hotel-paid deal${minimumStay}`;
	return `${hotelName} — ${formatDayYear(night.statDate)}: a deal at about ${Math.round(night.percentage)}% off${minimumStay}`;
};
